import { useEffect, useState } from 'react';
import { validateVariants, variantLabel } from '../../utils/productVariants';
function Preview({
  image
}) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    const local = image?.file instanceof File ? URL.createObjectURL(image.file) : '';
    setSrc(local || image?.url || image || '');
    return () => {
      if (local) URL.revokeObjectURL(local);
    };
  }, [image]);
  return src ? <img src={src} alt="Variation preview" className="h-20 w-20 rounded-lg border object-contain" /> : null;
}
export default function ProductVariationsEditor({
  values,
  onChange
}) {
  const rows = values.variants || [];
  const suggested = /fashion|cloth|apparel/i.test(values.category || '') ? ['Color', 'Size'] : /electronic/i.test(values.category || '') ? ['Color', 'Capacity'] : ['Option'];
  const [names, setNames] = useState(() => rows.length ? Object.keys(rows[0].options) : suggested);
  const [uploadError, setUploadError] = useState('');
  const update = (index, patch) => onChange({
    variants: rows.map((row, i) => i === index ? {
      ...row,
      ...patch
    } : row)
  });
  const error = validateVariants(rows);
  return <section className="mb-8 space-y-5 rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
    <div><h3 className="text-xl font-bold">Product variations</h3><p className="mt-2 text-sm text-slate-600">Optional: give each size, color, capacity or model its own price, stock and photos. Products without variations keep their usual price and stock.</p></div>
    {!rows.length && (values.productImages || []).length>0 && <fieldset className="rounded-xl border bg-white p-4"><legend className="px-2 font-semibold">Photos in See more</legend><p className="mb-3 text-sm text-slate-600">Select the main product photos to show individually at large size.</p><div className="flex flex-wrap gap-4">{values.productImages.map((image,i)=><label key={i} className="text-xs"><Preview image={image}/><input type="checkbox" checked={(values.detailImageIndexes||[]).includes(i)} onChange={event=>onChange({detailImageIndexes:event.target.checked?[...(values.detailImageIndexes||[]),i].sort((a,b)=>a-b):(values.detailImageIndexes||[]).filter(index=>index!==i)})}/> Show photo {i+1} in See more</label>)}</div></fieldset>}
    <div className="flex flex-wrap items-end gap-3">{names.map((name, i) => <label key={i} className="text-sm">Option {i + 1}<input aria-label={`Option ${i + 1} name`} value={name} maxLength={40} onChange={e => {
          const next = [...names];
          next[i] = e.target.value;
          if (rows.length && (next.some(key => !key.trim()) || new Set(next.map(key => key.trim().toLowerCase())).size !== next.length)) return;
          setNames(next);
          onChange({
            variants: rows.map(row => ({
              ...row,
              options: Object.fromEntries(next.map((key, j) => [key, Object.values(row.options)[j] || '']))
            }))
          });
        }} className="mt-1 block w-36 rounded-lg border p-2" /></label>)}
    {names.length < 3 && !names.includes(`Option ${names.length + 1}`) && <button type="button" onClick={() => {
        const name = `Option ${names.length + 1}`;
        setNames([...names, name]);
        onChange({
          variants: rows.map(row => ({
            ...row,
            options: {
              ...row.options,
              [name]: ''
            }
          }))
        });
      }} className="rounded-lg border bg-white px-3 py-2 text-sm">Add option type</button>}</div>
    {values.wholesaleEnabled && <p className="text-sm text-slate-600">The product’s wholesale price and minimum apply separately to each selected variation.</p>}
    <div className="space-y-4">{rows.map((row, index) => <fieldset key={row.id} className="rounded-xl border bg-white p-4"><legend className="px-2 font-semibold">Variation {index + 1}</legend>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{names.map((name, i) => <label key={i} className="text-sm">{name || `Option ${i + 1}`}<input aria-label={`Variation ${index + 1} ${name}`} value={row.options[name] || ''} maxLength={80} onChange={e => update(index, {
              options: {
                ...row.options,
                [name]: e.target.value
              }
            })} className="mt-1 block w-full rounded-lg border p-2" /></label>)}
      {[['price', 'Selling price (LKR)'], ['originalPrice', 'List price (optional)'], ['stock', 'Stock quantity'], ['sku', 'SKU (optional)']].map(([key, label]) => <label key={key} className="text-sm">{label}<input aria-label={`Variation ${index + 1} ${label}`} type={key === 'sku' ? 'text' : 'number'} min="0" step={key === 'stock' ? '1' : '0.01'} value={row[key] ?? ''} onChange={e => update(index, {
              [key]: e.target.value
            })} className="mt-1 block w-full rounded-lg border p-2" /></label>)}
      </div>
      <label className="mt-4 block text-sm">Photos for this variation (up to 8)<input aria-label={`Variation ${index + 1} photos`} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={e => {
            const files = Array.from(e.target.files || []);
            e.target.value = '';
            if (files.length + (row.images || []).length > 8 || files.some(file => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size >= 10 * 1024 * 1024)) {
              setUploadError('Choose up to 8 JPG, PNG or WebP images per variation, each under 10 MB.');
              return;
            }
            setUploadError('');
            update(index, {
              images: [...(row.images || []), ...files.map(file => ({
                file,
                url: ''
              }))]
            });
          }} className="mt-2 block w-full" /></label>
      <div className="mt-3 flex flex-wrap gap-3">{(row.images || []).map((image, i) => <div key={i}><Preview image={image} /><label className="mt-2 block text-xs"><input type="checkbox" aria-label={`Variation ${index + 1} photo ${i + 1} in See more`} checked={(row.detailImageIndexes || []).includes(i)} onChange={event => update(index, {detailImageIndexes: event.target.checked ? [...(row.detailImageIndexes || []), i].sort((a,b)=>a-b) : (row.detailImageIndexes || []).filter(value=>value!==i)})} /> Show in See more</label><button type="button" className="mt-1 text-xs text-red-700" onClick={() => update(index, {
              images: row.images.filter((_, j) => j !== i),
              detailImageIndexes: (row.detailImageIndexes || []).filter(value=>value!==i).map(value=>value>i?value-1:value)
            })}>Remove photo {i + 1}</button></div>)}</div>
      <p className="mt-2 text-xs text-slate-500">Select the photos to display at large size in See more for this variation. First photo is the variation thumbnail. Without photos, the main product gallery is used.</p>
      <div className="mt-4 flex items-center justify-between"><label className="flex gap-2 text-sm"><input type="checkbox" checked={row.enabled !== false} onChange={e => update(index, {
              enabled: e.target.checked
            })} />Available for sale</label><button type="button" aria-label={`Remove variation ${index + 1}`} className="text-sm text-red-700" onClick={() => onChange({
            variants: rows.filter((_, i) => i !== index)
          })}>Remove variation</button></div>
      <p className="sr-only">{variantLabel(row)}</p>
    </fieldset>)}</div>
    {uploadError && <p role="alert" className="text-sm text-red-700">{uploadError}</p>}
    {error && <p role="status" className="text-sm text-amber-800">{error}</p>}
    <button type="button" disabled={rows.length >= 100 || names.some(name => !name.trim()) || new Set(names).size !== names.length} onClick={() => onChange({
      variants: [...rows, {
        id: crypto.randomUUID(),
        options: Object.fromEntries(names.map(name => [name, ''])),
        price: values.price ?? '',
        originalPrice: '',
        stock: 0,
        sku: '',
        images: [],
        enabled: true
      }]
    })} className="rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">Add variation</button>
  </section>;
}
